import { apiClient } from '@/lib/api-client';
import type {
  SignInResponse,
  ForgotPasswordInput,
  ResetPasswordInput,
  ChangePasswordInput,
  MyPerksResponse,
} from '../../domain/entities/auth.entity';

export class AuthApiDatasource {
  /**
   * Entrada no painel — `/auth/federated-entry`, **não** `/auth/sign-in`.
   *
   * A diferença é o desfecho de quem não tem perfil neste scope: o `sign-in`
   * responde 200 com `platformProfile: null`, e a pessoa cai no painel sem
   * papel nenhum, vendo uma tela vazia sem saber por quê. A rota nova recusa
   * com **403 `PLATFORM_PROFILE_REQUIRED`** (ou `REGISTRATION_REQUIRED`, quando
   * nem conta existe), que o `useSignIn` traduz em mensagem.
   *
   * Vale para **toda** entrada daqui, não só a do Google: o painel administrativo
   * exige perfil neste scope seja qual for a forma de login, então o 200
   * silencioso é sempre a resposta errada para ele.
   */
  async enterPlatform(firebaseToken: string): Promise<SignInResponse> {
    const { data } = await apiClient.post<SignInResponse>(
      '/auth/federated-entry',
      { firebaseUser: firebaseToken },
    );
    return data;
  }

  /**
   * Roles/perks atuais do usuário logado — sem refazer o sign-in. Sem
   * `scopeId`, no scope deste app; com ele, no de outra plataforma: o editor de
   * acessos mede a autoridade de quem concede também no scope do perfil-alvo.
   */
  async getMyPerks(scopeId?: string): Promise<MyPerksResponse> {
    const { data } = await apiClient.get<MyPerksResponse>(
      '/auth/me/perks',
      scopeId ? { headers: { 'x-scope-id': scopeId } } : undefined,
    );
    return data;
  }

  async forgotPassword(input: ForgotPasswordInput): Promise<void> {
    await apiClient.post('/auth/forgot-password', input);
  }

  async resetPassword(input: ResetPasswordInput): Promise<void> {
    await apiClient.post('/auth/reset-password', input);
  }

  /**
   * O corpo é **só** `{ newPassword }`. O `ChangePasswordRequestDto` do monólito
   * não tem `currentPassword`, e o `ValidationPipe` global roda com
   * `forbidNonWhitelisted: true` — mandar o campo a mais devolve 400
   * ("property currentPassword should not exist"), não é ignorado.
   *
   * A senha atual não vai para o backend em nenhum momento: ela serve à
   * reautenticação no Firebase, e é o `auth_time` recente que dela resulta que o
   * `RecentAuthGuard` exige.
   */
  async changePassword(input: ChangePasswordInput): Promise<void> {
    await apiClient.post('/auth/change-password', {
      newPassword: input.newPassword,
    });
  }

  /**
   * Pede ao gateway um custom token para o usuário logado (SSO handoff → user
   * dashboard). O interceptor do apiClient anexa o Firebase ID token no Bearer.
   */
  async createHandoffToken(): Promise<{ customToken: string }> {
    const { data } = await apiClient.post<{ customToken: string }>(
      '/auth/handoff',
      {},
    );
    return data;
  }
}
